/**
 * "Your details" inside the donor sheet: name, email (and confirming it), home address, and how
 * the donor pays. Moved here unchanged from `DonorPanel.tsx` (GH-856) to keep that file under the
 * size ceiling once sign-in and receipts joined it; the sheet is still its only host.
 */
import { useEffect, useState } from "react";
import {
  ANONYMITY_COPY,
  type DonorPaymentMethodsResponse,
  type DonorSessionResponse,
} from "@givehere/contracts";
import {
  confirmEmailVerification,
  DonorApiError,
  fetchPaymentMethods,
  removeInstrument,
  setPaymentPreference,
  startEmailVerification,
} from "./donor-session.js";
import { Explain } from "./Explain.js";
import { instrumentLabel } from "./PaymentSheet.js";

/* ------------------------------------------------------------------- profile */

export function ProfileStep({
  session,
  token,
  busy,
  onSave,
  onSkip,
  onAdvancedPrivacy,
}: {
  session: DonorSessionResponse;
  token: string;
  busy: boolean;
  onSave: (details: {
    displayName?: string;
    email?: string;
    postalAddress?: {
      line1: string;
      line2: string | null;
      city: string;
      region: string;
      postalCode: string;
    };
  }) => void;
  onSkip: () => void;
  /**
   * The way to "Advanced privacy" (GH-858) — the deepest level of the sheet, and the only way to
   * "Anonymous to all organizations". Last on this screen, below Save, on purpose.
   */
  onAdvancedPrivacy: () => void;
}) {
  const [displayName, setDisplayName] = useState(session.profile.displayName ?? "");
  const [email, setEmail] = useState(session.profile.email ?? "");
  const [addressOpen, setAddressOpen] = useState(session.profile.postalAddress !== null);
  const [line1, setLine1] = useState(session.profile.postalAddress?.line1 ?? "");
  const [city, setCity] = useState(session.profile.postalAddress?.city ?? "");
  const [region, setRegion] = useState(session.profile.postalAddress?.region ?? "");
  const [postalCode, setPostalCode] = useState(session.profile.postalAddress?.postalCode ?? "");

  return (
    <>
      <h2 id="donor-title" className="donor__title">
        Your details
      </h2>
      <Explain className="donor__where" label="All of this is optional" title="Your details">
        <p>All of this is optional. It stays with your profile.</p>
      </Explain>

      <label className="field">
        <span>Name</span>
        <input
          autoComplete="name"
          value={displayName}
          onChange={(changed) => setDisplayName(changed.target.value)}
        />
      </label>
      <label className="field">
        <span>Email</span>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(changed) => setEmail(changed.target.value)}
        />
      </label>
      <ConfirmEmail
        token={token}
        email={email.trim()}
        alreadyConfirmed={
          session.profile.emailVerified && email.trim() === (session.profile.email ?? "")
        }
      />

      {!addressOpen ? (
        <button type="button" className="link" onClick={() => setAddressOpen(true)}>
          Add my home address (optional)
        </button>
      ) : (
        <>
          <label className="field">
            <span>Street address</span>
            <input
              autoComplete="street-address"
              value={line1}
              onChange={(changed) => setLine1(changed.target.value)}
            />
          </label>
          <div className="row">
            <label className="field">
              <span>City</span>
              <input value={city} onChange={(changed) => setCity(changed.target.value)} />
            </label>
            <label className="field">
              <span>State</span>
              <input value={region} onChange={(changed) => setRegion(changed.target.value)} />
            </label>
            <label className="field">
              <span>ZIP</span>
              <input
                autoComplete="postal-code"
                value={postalCode}
                onChange={(changed) => setPostalCode(changed.target.value)}
              />
            </label>
          </div>
        </>
      )}

      <PaymentMethodsSection token={token} />

      <button
        type="button"
        className="pay"
        disabled={busy}
        onClick={() =>
          onSave({
            ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
            ...(email.trim() ? { email: email.trim() } : {}),
            ...(addressOpen && line1.trim() && city.trim() && region.trim() && postalCode.trim()
              ? {
                  postalAddress: {
                    line1: line1.trim(),
                    line2: null,
                    city: city.trim(),
                    region: region.trim(),
                    postalCode: postalCode.trim(),
                  },
                }
              : {}),
          })
        }
      >
        {busy ? "Saving…" : "Save"}
      </button>
      <button type="button" className="link" onClick={onSkip}>
        Skip for now
      </button>

      <div className="donor__more">
        <button type="button" className="link" onClick={onAdvancedPrivacy}>
          {ANONYMITY_COPY.advancedPrivacyLabel}
        </button>
      </div>
    </>
  );
}

/**
 * How you pay. There is nothing stored that could leak — no card number, no wallet token —
 * and this section says so instead of pretending to be a card manager. What the donor controls
 * is which way of paying their next Give button leads with.
 */
function PaymentMethodsSection({ token }: { token: string }) {
  const [known, setKnown] = useState<DonorPaymentMethodsResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPaymentMethods(token)
      .then((loaded) => {
        if (!cancelled) setKnown(loaded);
      })
      // Load failure hides an optional section — the profile still works without it.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token]);

  const [removing, setRemoving] = useState<string | null>(null);

  if (!known || (known.methods.length === 0 && known.instruments.length === 0)) return null;

  const remove = (instrumentId: string) => {
    setRemoving(instrumentId);
    setSaveError(null);
    removeInstrument(token, instrumentId)
      .then(() => fetchPaymentMethods(token))
      .then(setKnown)
      .catch(() => setSaveError("That didn't save — check your connection and try again."))
      .finally(() => setRemoving(null));
  };

  const label = (method: string) =>
    method === "apple_pay" ? "Apple Pay" : method === "google_pay" ? "Google Pay" : "Card";

  const pick = (method: "card" | "apple_pay" | "google_pay" | null) => {
    setSaving(true);
    setSaveError(null);
    setPaymentPreference(token, method)
      .then(() => fetchPaymentMethods(token))
      .then(setKnown)
      // A save the donor asked for must never fail into silence: the tap did nothing, so say so.
      .catch(() => setSaveError("That didn't save — check your connection and try again."))
      .finally(() => setSaving(false));
  };

  return (
    <div className="paymethods">
      <h3 className="paymethods__title">How you pay</h3>
      {known.instruments.length > 0 && (
        <ul className="paymethods__list">
          {known.instruments.map((instrument) => (
            <li key={instrument.id} className="paymethods__row">
              <span>
                <strong>{instrumentLabel(instrument)}</strong>
                <span className="paymethods__meta">
                  {" "}
                  · saved for one-tap
                  {instrument.expMonth && instrument.expYear
                    ? ` · exp ${instrument.expMonth}/${String(instrument.expYear).slice(-2)}`
                    : ""}
                </span>
              </span>
              <button
                type="button"
                className="link"
                disabled={removing === instrument.id}
                onClick={() => remove(instrument.id)}
              >
                {removing === instrument.id ? "Removing…" : "Remove"}
              </button>
            </li>
          ))}
        </ul>
      )}
      <ul className="paymethods__list">
        {known.methods.map((used) => (
          <li key={used.method} className="paymethods__row">
            <span>
              <strong>{label(used.method)}</strong>
              <span className="paymethods__meta">
                {" "}
                · last used {new Date(used.lastUsedAt).toLocaleDateString()}
              </span>
            </span>
            {known.defaultMethod === used.method ? (
              <span className="paymethods__default">default</span>
            ) : (
              <button
                type="button"
                className="link"
                disabled={saving}
                onClick={() => pick(used.method)}
              >
                Make default
              </button>
            )}
          </li>
        ))}
      </ul>
      {saveError && (
        <p className="notice notice--error" role="alert">
          {saveError}
        </p>
      )}
      {known.preferred && (
        <button type="button" className="link" disabled={saving} onClick={() => pick(null)}>
          Clear my choice — just use whatever I paid with last
        </button>
      )}
      <p className="paymethods__note">
        We never store card numbers or wallet details — only which way you paid.
      </p>
    </div>
  );
}

/**
 * "Is this address really yours?" — asked here, answered here, and never in the way.
 *
 * Owner directive, 2026-08-25: everyone who enters an address is asked to confirm it right away.
 * The shape of *asking* matters as much as the asking. This is a link and, once pressed, a code
 * box — never a modal, never a step in the donation flow, and it never disables Save. A donor who
 * ignores it entirely keeps their address, their receipts, their history and their ability to
 * give, exactly as before.
 *
 * The address is read from the live input rather than the saved profile, so somebody who has just
 * corrected a typo confirms the corrected address instead of the stale one.
 */
function ConfirmEmail({
  token,
  email,
  alreadyConfirmed,
}: {
  token: string;
  email: string;
  alreadyConfirmed: boolean;
}) {
  const [stage, setStage] = useState<"idle" | "sending" | "code" | "done">("idle");
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!email) return null;
  if (alreadyConfirmed || stage === "done") {
    return <p className="donor__where">Email confirmed</p>;
  }

  const send = async () => {
    setStage("sending");
    setError(null);
    try {
      const started = await startEmailVerification(token, email);
      setVerificationId(started.verificationId);
      // Where nothing can be delivered — local development — the server returns the code rather
      // than leaving the box unanswerable. It is never returned on a deployment that can send.
      if (started.developmentCode) setCode(started.developmentCode);
      setStage("code");
    } catch (caught) {
      setError(caught instanceof DonorApiError ? caught.message : "That didn't send — try again.");
      setStage("idle");
    }
  };

  const confirm = async () => {
    if (!verificationId) return;
    setError(null);
    try {
      await confirmEmailVerification(token, { verificationId, code: code.trim(), email });
      setStage("done");
    } catch (caught) {
      setError(caught instanceof DonorApiError ? caught.message : "That code did not work.");
    }
  };

  if (stage === "code") {
    return (
      <div className="field">
        <span>Enter the code we sent to {email}</span>
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(changed) => {
            setError(null);
            setCode(changed.target.value);
          }}
        />
        <button type="button" className="link" onClick={() => void confirm()}>
          Confirm
        </button>
        {error && (
          <p className="donor__where" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="link"
        disabled={stage === "sending"}
        onClick={() => void send()}
      >
        {stage === "sending" ? "Sending…" : "Confirm this email address"}
      </button>
      {error && (
        <p className="donor__where" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
